import {
	Breakpoints,
	Container,
	ContainerGutter,
	CTA,
	Spinner,
	Text,
	TextAlignment,
	ThemeProvider,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { Download, LinkExternal } from '@volkswagen-onehub/icons-core';
import React from 'react';
import styled from 'styled-components';
import { HuchaValues } from '../../../models/hucha-values';
import { useHuchaPopup } from '../../../hooks/use-hucha-popup';
import { HuchaDisclaimer1Text } from '../../disclaimers/hucha-disclaimers';
import { useHuchaPdfGenerator } from '../../../hooks/use-hucha-pdf-generator';
import { HuchaPDFContent } from '../../pdf/hucha-pdf-content';
import { handleScrollToDisclaimer } from '../../../../../../helpers/disclaimers/disclaimers-utils';
import { StyledReferenceBadge } from '../renovadores-bonus-card/renovadores-bonus-card-c2';
import { useDisclaimers } from '../../../../../../context/disclaimers-context';

const MainContainer = styled(Container)`
	padding: 0px;

	/* Mobile */
	@media (max-width: 959px) {
		padding-top: 40px;
	}

	/* Tablet & Desktop */
	@media (min-width: 960px) {
		padding-top: 0px;
		padding-left: var(--size-dynamic0100);
	}
`;

const StyledC3Wrapper = styled.div`
	border: 2px solid #00b0f0;
	border-radius: 16px;
	align-content: center;
	padding-left: var(--size-dynamic0130);
	padding-right: var(--size-dynamic0130);

	/* Mobile */
	@media (max-width: 959px) {
		height: 364px;
	}

	/* Tablet */
	@media (min-width: 960px) {
		height: 193px;
	}

	/* Desktop */
	@media (min-width: 1600px) {
		height: 193px;
	}
`;

const TextSuperLarge = styled(Text)`
	font-size: 112px;
	line-height: 100%;
	// cambiar tamaño del texto cuando el número tenga > 4 dígitos ?
`;

const SymbolText = styled(Text)`
	font-size: 56px;
	line-height: 120%;
`;

const StyledCTA = styled(CTA)`
	@media (max-width: 959px) {
		width: 100%;
	}
`;

export const HuchaBonusCardC3: React.FC<{
	huchaValues: HuchaValues | null;
}> = ({ huchaValues }) => {
	const targetRefPDF = React.useRef<HTMLDivElement>(null);
	const { handlePdfDownloadClick, isDownloading, isPdfReady } = useHuchaPdfGenerator(huchaValues, targetRefPDF);
	const { handlePopupOpen } = useHuchaPopup(huchaValues);

	const { findBadge } = useDisclaimers();
	const badge = findBadge(HuchaDisclaimer1Text);

	return (
		<>
			<MainContainer stretchContent data-testid="c3-container">
				<StyledC3Wrapper>
					<Container
						gutter={ContainerGutter.static450}
						verticalAlign="center"
						stretchContent
						horizontalAlign="center"
						data-testid="c3-inner-container"
						wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b960]: 'never' }}
					>
						<Container wrap="always" verticalAlign="center" horizontalAlign="center">
							<Text
								appearance={TokenTextAppearance.headline200}
								color={TokenTextColor.inverted}
								textAlign={TextAlignment.center}
								bold
							>
								{'Llevas acumulado'}
							</Text>
							<Container wrap="never" horizontalAlign="center">
								<Container wrap="never" verticalAlign="flex-end">
									<TextSuperLarge color={TokenTextColor.inverted}>{huchaValues?.dineroHucha || '0'}</TextSuperLarge>
									<SymbolText color={TokenTextColor.inverted}>{'€'}</SymbolText>
								</Container>
								{badge && (
									<StyledReferenceBadge
										variant="large"
										onClick={() => {
											if (badge) handleScrollToDisclaimer(badge);
										}}
									>
										{badge.replace(/\.$/, '')}
									</StyledReferenceBadge>
								)}
							</Container>
						</Container>

						<ThemeProvider theme="inverted">
							<Container
								wrap="always"
								stretchContent
								verticalAlign="center"
								horizontalAlign="center"
								gutter={ContainerGutter.static300}
							>
								<StyledCTA
									tag="button"
									emphasis="secondary"
									onClick={handlePdfDownloadClick}
									icon={isDownloading ? <Spinner /> : <Download variant="small" />}
								>
									{isDownloading ? 'Descargando' : 'Descargar ahorro acumulado'}
								</StyledCTA>
								<StyledCTA
									tag="button"
									emphasis="primary"
									onClick={handlePopupOpen}
									icon={<LinkExternal variant="small" />}
								>
									Más sobre tus ahorros
								</StyledCTA>
							</Container>
						</ThemeProvider>
					</Container>
				</StyledC3Wrapper>
			</MainContainer>
			{isPdfReady && <HuchaPDFContent targetRef={targetRefPDF} huchaValues={huchaValues} />}
		</>
	);
};
